import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: string;
  completed: boolean;
  content: {
    title: string;
    description: string;
    features?: Array<{
      icon: string;
      title: string;
      description: string;
    }>;
    code?: string;
    action?: {
      label: string;
      type: 'primary' | 'secondary';
    };
  };
}

interface OnboardingState {
  isOpen: boolean;
  currentStep: number;
  steps: OnboardingStep[];
  hasCompletedOnboarding: boolean;

  // Actions
  openOnboarding: () => void;
  closeOnboarding: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (stepIndex: number) => void;
  completeStep: (stepId: string) => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  skipOnboarding: () => void;
}

const defaultSteps: OnboardingStep[] = [
  {
    id: 'connect-database',
    title: 'Connect Your Database',
    description: 'Start by connecting your first database',
    icon: 'Database',
    completed: false,
    content: {
      title: 'Connect to Your Database',
      description: 'Moniva supports PostgreSQL, MySQL, and SQLite databases. Connect your database to start exploring your data.',
      features: [
        {
          icon: 'Database',
          title: 'Multiple Database Types',
          description: 'Support for PostgreSQL, MySQL, and SQLite with easy connection setup',
        },
        {
          icon: 'Shield',
          title: 'Secure Connections',
          description: 'SSL/TLS encryption support for secure database connections',
        },
        {
          icon: 'Zap',
          title: 'Quick Templates',
          description: 'Use pre-configured templates for local development',
        },
      ],
      action: {
        label: 'Connect Database',
        type: 'primary',
      },
    },
  },
  {
    id: 'schema-designer',
    title: 'Schema Designer',
    description: 'Design your database visually',
    icon: 'Blocks',
    completed: false,
    content: {
      title: 'Visual Schema Designer',
      description: 'Design and plan your database schema visually before implementation. Create tables, define columns, set relationships, and generate SQL or TypeScript code automatically.',
      features: [
        {
          icon: 'MousePointer',
          title: 'Drag & Drop Designer',
          description: 'Create tables and relationships with an intuitive visual interface',
        },
        {
          icon: 'Code',
          title: 'Code Generation',
          description: 'Automatically generate SQL DDL statements and TypeScript type definitions from your design',
        },
        {
          icon: 'GitBranch',
          title: 'Relationship Designer',
          description: 'Define foreign keys, one-to-many, and many-to-many relationships visually',
        },
      ],
      action: {
        label: 'Try Schema Designer',
        type: 'primary',
      },
    },
  },
  {
    id: 'visual-query-builder',
    title: 'Visual Query Builder',
    description: 'Build queries without writing SQL',
    icon: 'Network',
    completed: false,
    content: {
      title: 'Build Queries Visually',
      description: 'Create complex SQL queries without writing code. Our visual query builder makes it easy for non-technical users to work with data.',
      features: [
        {
          icon: 'MousePointer',
          title: 'Drag & Drop Interface',
          description: 'Simply drag tables and columns to build your queries visually',
        },
        {
          icon: 'Filter',
          title: 'Smart Filters',
          description: 'Add conditions, joins, and aggregations with an intuitive interface',
        },
        {
          icon: 'Code',
          title: 'SQL Preview',
          description: 'See the generated SQL in real-time and learn as you build',
        },
      ],
      action: {
        label: 'Try Query Builder',
        type: 'primary',
      },
    },
  },
  {
    id: 'run-queries',
    title: 'Run SQL Queries',
    description: 'Execute queries with our powerful editor',
    icon: 'Play',
    completed: false,
    content: {
      title: 'Powerful Query Editor',
      description: 'For advanced users, write and execute SQL queries with syntax highlighting, auto-completion, and intelligent suggestions.',
      features: [
        {
          icon: 'Sparkles',
          title: 'AI-Powered Assistance',
          description: 'Get query suggestions and help from our AI assistant',
        },
        {
          icon: 'BookmarkCheck',
          title: 'Save & Organize',
          description: 'Save frequently used queries and organize them into collections',
        },
        {
          icon: 'Clock',
          title: 'Query History',
          description: 'Access your query history and re-run previous queries',
        },
      ],
      code: 'SELECT users.name, orders.total\nFROM users\nJOIN orders ON users.id = orders.user_id\nWHERE orders.status = \'completed\'\nORDER BY orders.total DESC\nLIMIT 10;',
      action: {
        label: 'Open Query Editor',
        type: 'primary',
      },
    },
  },
  {
    id: 'visualize-data',
    title: 'Visualize Your Data',
    description: 'Create charts and dashboards',
    icon: 'BarChart',
    completed: false,
    content: {
      title: 'Data Visualization',
      description: 'Transform your query results into beautiful charts and graphs. Create dashboards to monitor your data insights.',
      features: [
        {
          icon: 'TrendingUp',
          title: 'Multiple Chart Types',
          description: 'Bar charts, line graphs, pie charts, and more to visualize your data',
        },
        {
          icon: 'Layout',
          title: 'Custom Dashboards',
          description: 'Combine multiple visualizations into comprehensive dashboards',
        },
        {
          icon: 'Share',
          title: 'Share Insights',
          description: 'Export and share your visualizations with your team',
        },
      ],
      action: {
        label: 'View Analytics',
        type: 'primary',
      },
    },
  },
  {
    id: 'collaborate',
    title: 'Collaborate with Team',
    description: 'Work together in real-time',
    icon: 'Users',
    completed: false,
    content: {
      title: 'Real-Time Team Collaboration',
      description: 'Moniva is like Figma for databases! See who\'s viewing your database, watch their cursor movements on the schema canvas, and get notified when teammates run queries.',
      features: [
        {
          icon: 'MousePointer',
          title: 'Live Cursor Tracking',
          description: 'See your teammates\' cursors moving in real-time on the Schema Visualizer tab',
        },
        {
          icon: 'UserPlus',
          title: 'Presence Indicators',
          description: 'Avatar stack shows who\'s currently viewing each database with you',
        },
        {
          icon: 'Share2',
          title: 'Query Notifications',
          description: 'Get instant notifications when team members execute queries on shared databases',
        },
      ],
      action: {
        label: 'Invite Team',
        type: 'primary',
      },
    },
  },
  {
    id: 'explore-features',
    title: 'Explore More Features',
    description: 'Discover advanced capabilities',
    icon: 'Rocket',
    completed: false,
    content: {
      title: 'Ready to Get Started!',
      description: 'You\'re all set! Explore Moniva\'s powerful features and start working with your data more effectively.',
      features: [
        {
          icon: 'Lightbulb',
          title: 'AI Query Assistant',
          description: 'Ask questions in natural language and get SQL queries generated for you',
        },
        {
          icon: 'FileText',
          title: 'Data Browser',
          description: 'Browse and edit your table data with a spreadsheet-like interface',
        },
        {
          icon: 'Settings',
          title: 'Advanced Settings',
          description: 'Customize Moniva to fit your workflow and preferences',
        },
      ],
      action: {
        label: 'Start Exploring',
        type: 'primary',
      },
    },
  },
];

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      isOpen: false,
      currentStep: 0,
      steps: defaultSteps,
      hasCompletedOnboarding: false,

      openOnboarding: () => set({ isOpen: true, currentStep: 0 }),

      closeOnboarding: () => set({ isOpen: false }),

      nextStep: () => {
        const { currentStep, steps } = get();
        if (currentStep < steps.length - 1) {
          set({ currentStep: currentStep + 1 });
        }
      },

      previousStep: () => {
        const { currentStep } = get();
        if (currentStep > 0) {
          set({ currentStep: currentStep - 1 });
        }
      },

      goToStep: (stepIndex: number) => {
        const { steps } = get();
        if (stepIndex >= 0 && stepIndex < steps.length) {
          set({ currentStep: stepIndex });
        }
      },

      completeStep: (stepId: string) => {
        set((state) => ({
          steps: state.steps.map((step) =>
            step.id === stepId ? { ...step, completed: true } : step
          ),
        }));
      },

      completeOnboarding: () => {
        set({
          hasCompletedOnboarding: true,
          isOpen: false,
          steps: get().steps.map((step) => ({ ...step, completed: true })),
        });
      },

      skipOnboarding: () => {
        set({
          hasCompletedOnboarding: true,
          isOpen: false,
        });
      },

      resetOnboarding: () => {
        set({
          currentStep: 0,
          steps: defaultSteps,
          hasCompletedOnboarding: false,
          isOpen: false,
        });
      },
    }),
    {
      name: 'moniva-onboarding',
      partialize: (state) => ({
        hasCompletedOnboarding: state.hasCompletedOnboarding,
        currentStep: state.currentStep,
        steps: state.steps.map((step) => ({
          id: step.id,
          completed: step.completed,
        })),
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Merge persisted completion status with default steps
          const mergedSteps = defaultSteps.map((defaultStep) => {
            const persistedStep = state.steps.find((s) => s.id === defaultStep.id);
            return {
              ...defaultStep,
              completed: persistedStep?.completed || false,
            };
          });
          state.steps = mergedSteps;
        }
      },
    }
  )
);
